import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Image } from 'react-native';
import Input from '../../common/Input';
import { palettes } from '../../../config';
import { getResponsiveStyle } from '../../../utils/getResponsiveStyle';
import Icon from 'react-native-vector-icons/Feather';
import Upload from '../../common/upload/upload';
import { useCreateMedia } from '../../../api/common/useCreateMedia';
import { useSetAlert } from '../../../hooks/useSetAlerts';

type ProductVariantCombinationCardProps = {
  associatedVariant: string;
  compareAtPrice?: number;
  image?: string;
  price?: number;
  quantity: number;
  text: string;
  variantType: string;
  variantKey: string;
  secondaryVariantType: string;
  onDelete?: (variantKey: string) => void;
  onQuantityChange: (variantKey: string, quantity: number) => void;
  onPriceChange?: (variantKey: string, price: number) => void;
  onImageUpload?: (variantKey: string, imageUrl: string) => void;
};

const ProductVariantCombinationCard: React.FC<ProductVariantCombinationCardProps> = ({
  associatedVariant,
  compareAtPrice,
  image,
  price = 0,
  quantity,
  text,
  variantType,
  variantKey,
  secondaryVariantType,
  onDelete,
  onQuantityChange,
  onPriceChange,
  onImageUpload,
}) => {
  const [quantityValue, setQuantityValue] = useState(quantity?.toString() || '0');
  const [priceValue, setPriceValue] = useState(price?.toString() || '0');
  const { _createMedia, loading: isUploading } = useCreateMedia({});
  const { setAlert } = useSetAlert();

  useEffect(() => {
    setQuantityValue(quantity?.toString() || '0');
  }, [quantity]);

  useEffect(() => {
    setPriceValue(price?.toString() || '0');
  }, [price]);

  const handleQuantityChange = (text: string) => {
    if (text === '' || /^[0-9]+$/.test(text)) {
      setQuantityValue(text);
      onQuantityChange(variantKey, text === '' ? 0 : parseInt(text, 10));
    }
  };

  const handlePriceChange = (text: string) => {
    if (text === '' || /^[0-9]*\.?[0-9]*$/.test(text)) {
      setPriceValue(text);
      if (onPriceChange) {
        onPriceChange(variantKey, text === '' ? 0 : parseFloat(text));
      }
    }
  };

  const handleFileUpload = (files: File[]) => {
    if (files && files.length > 0 && onImageUpload) {
      _createMedia({
        variables: {
          file: files[0],
          category: 'PRODUCT',
          alt: `Variant ${variantType}-${text}`,
        },
        onCompleted: (data) => {
          if (data?.createMedia?.data?.src) {
            // Get the actual image URL from the server response
            const imageUrl = data.createMedia.data.src;
            // Send it back to the parent component
            onImageUpload(variantKey, imageUrl);
            setAlert('Image uploaded successfully', 'normal');
          } else {
            setAlert('Failed to upload image', 'danger');
          }
        },
        onError: (error) => {
          console.error('Upload error:', error);
          setAlert('Failed to upload image: ' + error.message, 'danger');
        }
      });
    }
  };

  return (
    <View style={styles.wrapper}>
      <View style={styles.primaryVariantContainer}>
        <View style={{ flexDirection: 'row', marginRight: 10 }}>
          <Text style={styles.variantType}>{variantType}:</Text>
          <Text style={styles.variantValue}>{text}</Text>
        </View>
        <View style={{ flexDirection: 'row' }}>
          <Text style={styles.variantType}>{secondaryVariantType}:</Text>
          <Text style={styles.variantValue}>{associatedVariant}</Text>
        </View>
      </View>

      <View style={styles.container}>
        <View style={styles.contentRow}>
          {/* Image section */}
          <View style={styles.imageSection}>
            {image ? (
              <View style={styles.imageContainer}>
                <Image source={{ uri: image }} style={styles.variantImage} />
                <TouchableOpacity 
                  style={styles.replaceImageButton}
                  onPress={() => {
                    // Clear the image to show the upload component again
                    if (onImageUpload) {
                      onImageUpload(variantKey, '');
                    }
                  }}
                >
                  <Icon name="edit-2" size={12} color={palettes.light[0]} />
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.imageUploadContainer}>
                <Upload
                  hint={isUploading ? "Uploading..." : "+"}
                  isShowUploadIcon={false}
                  onDrop={handleFileUpload}
                  dropzoneStyles={styles.dropzone}
                  loading={isUploading}
                />
              </View>
            )}
          </View>

          {/* Quantity input */}
          <View style={styles.compactInputContainer}>
            <Text style={styles.inputLabel}>Qty:</Text>
            <Input
              value={quantityValue}
              onChangeText={handleQuantityChange}
              keyboardType="numeric"
              containerStyles={styles.quantityInput}
              style={styles.input}
            />
          </View>

          {/* Price input */}
          <View style={styles.compactInputContainer}>
            <Text style={styles.inputLabel}>Price:</Text>
            <Input
              value={priceValue}
              onChangeText={handlePriceChange}
              keyboardType="numeric"
              containerStyles={styles.priceInput}
              style={styles.input}
            />
          </View>

          {/* Delete button */}
          {onDelete && (
            <TouchableOpacity style={styles.deleteButton} onPress={() => onDelete(variantKey)}>
              <Icon name="trash-2" size={18} color={palettes.red[0]} />
            </TouchableOpacity>
          )}
        </View>

        {/* Compare price - moved below for space */}
        {compareAtPrice !== undefined && compareAtPrice > 0 && (
          <Text style={styles.comparePrice}>Was: {compareAtPrice}</Text>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    marginBottom: 8,
    borderRadius: 6,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: palettes.grey[5],
  },
  primaryVariantContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: palettes.grey[6],
  },
  primaryVariantLabel: {
    fontSize: getResponsiveStyle(14, 'font'),
    fontWeight: 'bold',
    color: palettes.dark[0],
    marginRight: 12,
  },
  variantType: {
    fontSize: getResponsiveStyle(13, 'font'),
    color: palettes.grey[1],
    marginRight: 4,
  },
  variantValue: {
    fontSize: getResponsiveStyle(13, 'font'),
    color: palettes.dark[0],
  },
  container: {
    backgroundColor: palettes.light[0],
    paddingBottom: 5,
  },
  contentRow: {
    flexDirection: 'row',
    padding: 10,
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 50,
    // maxHeight: 70,
  },
  imageSection: {
    width: 40,
    height: 40,
    marginRight: 10,
  },
  imageContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
    borderRadius: 4,
    overflow: 'hidden',
  },
  variantImage: {
    width: '100%',
    height: '100%',
    borderRadius: 4,
  },
  imageUploadContainer: {
    width: '100%',
    height: '100%',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: palettes.grey[4],
    overflow: 'hidden',
  },
  dropzone: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  replaceImageButton: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    opacity: 0,
    // Initially hidden, show on hover
    '&:hover': {
      opacity: 1,
    },
  },
  compactInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 8,
  },
  inputLabel: {
    fontSize: 13,
    marginRight: 4,
    color: palettes.grey[1],
  },
  quantityInput: {
    width: 80,
  },
  priceInput: {
    width: 80,
  },
  input: {
    textAlign: 'center',
    height: 32,
    fontSize: 13,
    paddingVertical: 0,
  },
  comparePrice: {
    fontSize: 12,
    color: palettes.grey[1],
    textDecorationLine: 'line-through',
    paddingLeft: 60,
    marginTop: -5,
    marginBottom: 5,
  },
  deleteButton: {
    width: 30,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default ProductVariantCombinationCard; 